import { motion } from "framer-motion";
import { ClipboardList, Flag, Repeat, Sparkles, Trophy, ArrowLeft, ArrowRight, Waves, ShieldCheck, Award, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import { Seo } from "@/components/site/Seo";
import Footer from "@/components/site/Footer";
import FAQ from "@/components/site/FAQ";
import WhereNext from "@/components/site/WhereNext";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const nextItems = [
  { sub: "See the full lesson pathway", label: "Our Swimming Lessons", to: "/classes" },
  { sub: "How lessons are structured", label: "What to Expect", to: "/how-it-works" },
  { sub: "View prices & packages", label: "Pricing", to: "/pricing" },
  { sub: "Speak to our team", label: "Contact Us", to: "/contact" },
];

const outcomes = [
  { icon: <ShieldCheck size={16} />, text: "Water safety from day one" },
  { icon: <Award size={16} />, text: "STA Swim Star stages" },
  { icon: <HeartHandshake size={16} />, text: "Small, supportive classes" },
];

const progress = [
  { icon: <ClipboardList size={22} />, text: "Structured lesson plans" },
  { icon: <Flag size={22} />, text: "Clear milestones and progression" },
  { icon: <Repeat size={22} />, text: "Consistent teaching approach" },
  { icon: <Sparkles size={22} />, text: "Confidence-led skill development" },
];

const stages = [
  { name: "Sharks 1", age: "4½ + · Beginner", desc: "Building water confidence and the basics of independent swimming." },
  { name: "Sharks 2", age: "4½ + · Can swim 5m unaided", desc: "Developing stroke technique, distance and control." },
  { name: "Sharks 3", age: "6 + · Can swim 10m unaided", desc: "Refining all four strokes, endurance and advanced water skills." },
];

const faqs = [
  {
    q: "Which class or stage is right for my child?",
    a: "New swimmers usually start in Sharks 1 and progress through Sharks 2 and Sharks 3 as their skills develop. We'll help place your child at the right level and move them up when they're ready.",
  },
  {
    q: "Do children swim independently?",
    a: "Yes — from age 4, children's lessons are independent, with no adult in the pool. Our qualified teachers work with small groups from the poolside, with safety cover in place at all times.",
  },
  {
    q: "What should my child wear and bring?",
    a: "A well-fitting swimsuit, goggles and a towel. Long hair should be tied back or worn in a swim cap. That's all they need to get going!",
  },
  {
    q: "How does progression work?",
    a: "We follow the STA Swim Star pathway, with clear stages and milestones, so children always know what they're working towards — and you can see real, measurable progress.",
  },
  {
    q: "What if we miss a lesson?",
    a: "Please let us know as early as you can. Get in touch with the team and we'll talk you through the options for your course.",
  },
  {
    q: "What if my child is ill?",
    a: "Please keep your child out of the pool if they have diarrhoea, sickness, a heavy cold or an ear infection, and wait 48 hours after the last episode of sickness or diarrhoea before returning.",
  },
];

export default function ChildrensSwimming() {
  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#334155] font-body" data-testid="childrens-swimming-page">
      <Seo
        title="Children's Swimming Lessons (4½+) | Happy Tiddlers Swim School"
        description="Confidence-led children's swimming lessons in Barton-le-Clay following the STA Swim Star Sharks pathway — water safety, stroke development and more."
        path="/classes/childrens-swimming"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-14 pb-20 md:pt-20 md:pb-24" data-testid="childrens-header">
        <div className="blob absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#0EA5E9]/10" />
        <div className="container mx-auto px-6 md:px-12 relative">
          <Link to="/classes" className="inline-flex items-center gap-2 text-[#0EA5E9] font-semibold hover:gap-3 transition-all mb-8" data-testid="childrens-back-link">
            <ArrowLeft size={18} /> All classes
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Children's Lessons • 4 years +</span>
              <h1 className="mt-4 font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-[#0F172A] leading-[1.05]">
                Building confident, capable swimmers
              </h1>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                Our children's lessons focus on developing strong swimming skills while keeping
                confidence and enjoyment at the heart of learning.
              </p>
              <p className="mt-4 text-lg text-[#334155] leading-relaxed">
                Children are encouraged, supported and challenged at the right level for them —
                never rushed, never compared.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/timetable" className="inline-flex items-center gap-2 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300" data-testid="childrens-availability-cta-top">
                  View class availability <ArrowRight size={18} />
                </Link>
                <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" className="bg-[#0EA5E9]/10 text-[#0EA5E9] font-bold px-8 py-4 rounded-full hover:bg-[#0EA5E9] hover:text-white transition-all duration-300" data-testid="childrens-book-cta-top">
                  Book now
                </a>
              </div>

              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="childrens-outcomes">
                {outcomes.map((o) => (
                  <span key={o.text} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-semibold text-[#0F172A] shadow-sm" data-testid="childrens-outcome">
                    <span className="text-[#0EA5E9]">{o.icon}</span>
                    {o.text}
                  </span>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="rounded-[2.5rem] overflow-hidden shadow-2xl shadow-[#0A2540]/20 rotate-2 hover:rotate-0 transition-transform duration-500">
              <img src="https://images.unsplash.com/photo-1651614158095-b98b6c1da74b" alt="Children swimming confidently in a lesson" className="w-full h-[380px] md:h-[520px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How children progress */}
      <section className="pb-20 md:pb-24" data-testid="childrens-progress">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-12">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">How children progress</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              A consistent, structured approach
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {progress.map((p, i) => (
              <motion.div key={p.text} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.1 }} className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-testid={`childrens-progress-${i}`}>
                <span className="grid place-items-center w-14 h-14 rounded-2xl mb-6 bg-[#0EA5E9] text-white">{p.icon}</span>
                <h3 className="font-heading text-lg font-semibold text-[#0F172A] leading-snug">{p.text}</h3>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 text-lg text-[#334155] leading-relaxed max-w-3xl">
            As confidence grows, technique follows — resulting in swimmers who feel capable, safe and proud of their achievements.
          </p>
        </div>
      </section>

      {/* Sharks pathway ladder */}
      <section className="pb-24 md:pb-32" data-testid="childrens-stages">
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-12">
            <span className="inline-flex items-center gap-2 text-sm text-[#FF7A1A] tracking-[0.2em] uppercase font-bold">
              <Trophy size={16} /> The Sharks pathway
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
              A clear ladder of progress
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              Children move up through our Sharks stages as their skills grow — always knowing what they're working towards next.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {stages.map((s, i) => (
              <motion.div key={s.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.1 }} className="relative bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-testid={`childrens-stage-${i}`}>
                <span className="font-heading text-6xl font-black text-[#1F7A8C]/15 leading-none">{i + 1}</span>
                <h3 className="mt-2 font-heading text-2xl font-semibold text-[#0F172A]">{s.name}</h3>
                <span className="inline-block mt-2 text-sm font-bold px-3 py-1 rounded-full bg-[#1F7A8C]/10 text-[#1F7A8C]">{s.age}</span>
                <p className="mt-3 text-[#334155] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-4 bg-[#0A2540] rounded-[1.5rem] p-6 md:p-7 text-white" data-testid="childrens-adults-note">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#14B8A6] shrink-0"><Waves size={24} /></span>
            <p className="text-base md:text-lg leading-relaxed">
              <span className="font-heading font-bold">Adults 15+</span> — lessons for every ability, from complete beginner to confident swimmer refining their technique.
            </p>
          </div>
        </div>
      </section>

      {/* A skill that lasts */}
      <section className="pb-24 md:pb-32" data-testid="childrens-skill-for-life">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-[2.5rem] overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="https://images.unsplash.com/photo-1639477651629-3afd5d79654d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="A confident young swimmer" className="w-full h-[380px] md:h-[460px] object-cover" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="order-1 lg:order-2">
              <span className="inline-flex items-center gap-2 text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">
                <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#0EA5E9] text-white"><Waves size={18} /></span>
                A skill that lasts
              </span>
              <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] leading-tight">
                More than strokes — a lifelong relationship with the water
              </h2>
              <p className="mt-6 text-lg text-[#334155] leading-relaxed">
                Swimming is a life skill. We support long-term development so children don't just
                "learn strokes", but build a lifelong relationship with the water — safe, capable and confident.
              </p>
              <Link to="/timetable" className="inline-flex items-center gap-2 mt-8 bg-[#FF7A1A] text-white font-bold px-8 py-4 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.5)] hover:-translate-y-1 transition-all duration-300" data-testid="childrens-availability-cta-bottom">
                View class availability <ArrowRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <FAQ items={faqs} intro="Thinking about children's lessons? Here's what parents most often ask." />
      <WhereNext items={nextItems} />
      <Footer />
    </div>
  );
}
